import axiosInstance from "./axiosConfig";

const authService = {
    async register(username, email, password, captchaResponse) {
        try {
            const response = await axiosInstance.post(`/auth/register`, {
                username,
                email,
                password,
                captcha_response: captchaResponse
            });
            return response.data;
        } catch (error) {
            console.error('Registration API error:', error.response?.data || error.message);
            throw error;
        }
    },

    async login(username, password, rememberMe = true) {
        try {
            const response = await axiosInstance.post(`/auth/login`, {
                username,
                password,
                remember_me: rememberMe
            });
            
            if (!response.data) {
                throw new Error('No response received from server');
            }

            if (response.data.token) {
                localStorage.setItem("token", response.data.token);
            }
            
            return response.data;
        } catch (error) {
            // Handle specific error responses
            if (error.response) {
                const { status, data } = error.response;
                
                // If the server provided an error message with details
                if (data?.error && data?.details) {
                    throw new Error(`${data.error}: ${data.details}`);
                }
                
                // Handle specific status codes
                if (status === 429) {
                    throw new Error('Account temporarily locked: Too many login attempts. Please try again in a few minutes.');
                }
                if (status === 403) {
                    throw new Error('Account disabled: Your account has been disabled. Please contact support for assistance.');
                }
                if (status === 401) {
                    if (data?.error === 'Account not found') {
                        throw new Error('Account not found: No account exists with this username');
                    }
                    if (data?.error === 'Incorrect password') {
                        throw new Error('Incorrect password: The password you entered is incorrect');
                    }
                    throw new Error('Invalid credentials: Please check your username and password');
                }
                if (status === 400) {
                    throw new Error('Invalid request: Please enter both username and password');
                }
                
                // If server provided an error message but no details
                if (data?.error) {
                    throw new Error(data.error);
                }
            }
            
            // Network or other errors
            if (error.request) {
                throw new Error('Unable to connect to the server. Please check your internet connection.');
            }
            
            throw new Error('An unexpected error occurred. Please try again.');
        }
    },

    async logout() {
        const response = await axiosInstance.post(`/auth/logout`);
        localStorage.clear("token");
        return response.data;
    },

    async getProfile() {
        try {
            const token = localStorage.getItem("token");
            const response = await axiosInstance.get(`/auth/profile`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            // If we get a 401, just return null instead of throwing
            if (error.response && error.response.status === 401) {
                return null;
            }
            console.error('Error fetching profile:', error.response?.data || error.message);
            return null; // Return null instead of throwing to prevent redirect loops
        }
    },

    async updateProfile(data) {
        const response = await axiosInstance.put(`/auth/profile`, data);
        return response.data;
    },

    async changePassword(currentPassword, newPassword) {
        const response = await axiosInstance.post(`/auth/change-password`, {
            current_password: currentPassword,
            new_password: newPassword
        });
        return response.data;
    },

    async getApiKey() {
        try {
            const response = await axiosInstance.get(`/auth/api-key`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                return { api_key: null };
            }
            throw error;
        }
    },

    validatePassword: (password) => {
        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        return (
            password.length >= minLength &&
            hasUpperCase &&
            hasLowerCase &&
            hasNumbers &&
            hasSpecialChar
        );
    },

    async generateApiKey() {
        const response = await axiosInstance.post(`/auth/api-key`);
        return response.data;
    },

    async revokeApiKey() {
        const response = await axiosInstance.delete(`/auth/api-key`);
        return response.data;
    },

    async getCaptcha() {
        try {
            const response = await axiosInstance.get(`/auth/captcha`);
            return response.data;
        } catch (error) {
            console.error('Captcha API error:', error.response?.data || error.message);
            throw error;
        }
    }
};

export default authService;